// =============================================================================
// features/admin/feature-flags/FeatureFlagsPage.tsx — Tela de administração de
// feature flags (F1-S23, estendida em F32-S04 com audience de organização e
// usuário — cidades e papéis passam a ter UI própria também).
//
// Funcionalidades:
//   - Tabela com filtro por status (enabled/disabled/internal_only).
//   - Toggle inline via PATCH /api/admin/feature-flags/:key.
//   - Ação "Editar" por linha abre o AudienceEditorDrawer (4 eixos + resumo
//     de impacto + semântica E/OU).
//   - Estados: carregando, sem permissão, erro recuperável (retry), vazio.
//
// `visible` (F43-S42): flags com `visible=false` nascem escuras de propósito
// (ex.: `waha.inbox.enabled`, aguardando DPIA — doc
// planejamento-inbox-vendedores-waha.md §6) e ficam FORA da lista por
// padrão — antes disso o campo não tinha nenhum efeito e qualquer titular de
// `flags:manage` conseguia ligar uma flag "escura" sem saber que ela exigia
// uma decisão prévia. Não usamos ocultação total: o checkbox "Mostrar flags
// ocultas" continua dando acesso deliberado a elas (elas seguem
// administráveis — só não aparecem por acidente durante uma varredura
// qualquer da tela). Ver F43-S42 para a lista de flags afetadas.
//
// Acesso: 'flags:manage' — checado no cliente (esconde a tela) e no backend
// (fonte de verdade real).
// =============================================================================

import * as React from 'react';

import { useAdminFeatureFlags, useUpdateFeatureFlag } from '../../../hooks/admin/useFeatureFlags';
import type { FeatureFlagDto, FeatureFlagStatus } from '../../../hooks/admin/useFeatureFlags.types';
import { useAuth } from '../../../lib/auth-store';

import { AudienceEditorDrawer } from './AudienceEditorDrawer';
import { FilterTabs, FlagsTable } from './FlagsTable';
import { buildStatusPatchBody, resolveStatusChange } from './statusChange';
import { StatusChangeConfirmDialog } from './StatusChangeConfirmDialog';

type StatusFilter = 'all' | FeatureFlagStatus;

/**
 * Aplica o filtro de visibilidade: flags com `visible=false` ficam fora por
 * padrão, a menos que `showHidden` esteja ligado. Exportada para teste
 * unitário puro (sem precisar montar a árvore de componentes).
 */
export function filterByVisibility(
  flags: readonly FeatureFlagDto[],
  showHidden: boolean,
): FeatureFlagDto[] {
  return showHidden ? [...flags] : flags.filter((f) => f.visible);
}

function NoPermissionState(): React.JSX.Element {
  return (
    <div className="flex items-center justify-center min-h-[320px]">
      <div className="flex flex-col items-center gap-3 text-center">
        <div
          className="flex items-center justify-center w-12 h-12 rounded-full"
          style={{ background: 'var(--surface-muted)' }}
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            className="w-6 h-6"
            style={{ color: 'var(--text-4)' }}
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" />
          </svg>
        </div>
        <p className="font-sans text-sm max-w-xs" style={{ color: 'var(--text-3)' }}>
          Você não tem permissão para gerenciar feature flags.
        </p>
      </div>
    </div>
  );
}

/**
 * Tela de administração de feature flags.
 * Requer permissão 'flags:manage' (checada aqui e no backend).
 */
export function FeatureFlagsPage(): React.JSX.Element {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('flags:manage');

  const [filter, setFilter] = React.useState<StatusFilter>('all');
  const [showHidden, setShowHidden] = React.useState(false);
  const [audienceFlag, setAudienceFlag] = React.useState<FeatureFlagDto | null>(null);
  const [pendingPromotion, setPendingPromotion] = React.useState<FeatureFlagDto | null>(null);

  const { data: flags, isLoading, isError, refetch } = useAdminFeatureFlags();
  const { update, pendingKey } = useUpdateFeatureFlag({ silent: true });
  // Mutation dedicada à confirmação de promoção: só fecha o diálogo em caso
  // de sucesso (padrão de AudienceEditorDrawer) — se a mutation falhar, o
  // diálogo permanece aberto e o toast de erro (disparado pelo hook mesmo em
  // modo silent) explica o motivo, em vez de fechar sem dizer o que houve.
  const { update: confirmPromotion, isPending: isConfirmingPromotion } = useUpdateFeatureFlag({
    silent: true,
    onSuccess: () => setPendingPromotion(null),
  });

  if (!canManage) {
    return <NoPermissionState />;
  }

  /**
   * Roteia todo pedido de mudança de status pela decisão pura de
   * `resolveStatusChange` — a única transição que exige confirmação
   * (`internal_only` → `enabled`) abre o diálogo em vez de aplicar direto.
   * Recusar a confirmação (fechar o diálogo) não chama `update`/`confirmPromotion`
   * nenhuma vez — nada muda.
   */
  function handleRequestStatusChange(
    key: string,
    from: FeatureFlagStatus,
    to: FeatureFlagStatus,
  ): void {
    const decision = resolveStatusChange(from, to);
    if (decision.kind === 'noop') return;
    if (decision.kind === 'confirm') {
      const flag = flags.find((f) => f.key === key) ?? null;
      setPendingPromotion(flag);
      return;
    }
    update(key, buildStatusPatchBody(decision.status));
  }

  function handleConfirmPromotion(): void {
    if (!pendingPromotion) return;
    confirmPromotion(pendingPromotion.key, buildStatusPatchBody('enabled'));
  }

  const hiddenCount = flags.filter((f) => !f.visible).length;
  const flagsByVisibility = filterByVisibility(flags, showHidden);

  const counts: Record<StatusFilter, number> = {
    all: flagsByVisibility.length,
    enabled: flagsByVisibility.filter((f) => f.status === 'enabled').length,
    disabled: flagsByVisibility.filter((f) => f.status === 'disabled').length,
    internal_only: flagsByVisibility.filter((f) => f.status === 'internal_only').length,
  };

  const visibleFlags =
    filter === 'all' ? flagsByVisibility : flagsByVisibility.filter((f) => f.status === filter);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1
          className="font-display font-bold text-ink"
          style={{
            fontSize: 'var(--text-3xl)',
            letterSpacing: '-0.03em',
            fontVariationSettings: "'opsz' 48",
          }}
        >
          Feature Flags
        </h1>
        <p className="font-sans text-sm mt-1" style={{ color: 'var(--text-3)' }}>
          Controle real em 4 camadas: UI, API, workers e tools de IA.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterTabs current={filter} counts={counts} onChange={setFilter} />

        {hiddenCount > 0 && (
          <label
            className="flex items-center gap-2 font-sans text-sm select-none"
            style={{ color: 'var(--text-3)' }}
          >
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              className="h-4 w-4 rounded"
            />
            Mostrar flags ocultas ({hiddenCount})
          </label>
        )}
      </div>

      <FlagsTable
        flags={visibleFlags}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        onRequestStatusChange={handleRequestStatusChange}
        togglePendingKey={pendingKey}
        onEditAudience={setAudienceFlag}
      />

      <p className="font-sans text-xs" style={{ color: 'var(--text-4)' }}>
        Cache client: 30s — alterações refletem em até 30s para todos os usuários.
      </p>

      <AudienceEditorDrawer
        open={audienceFlag !== null}
        onClose={() => setAudienceFlag(null)}
        flag={audienceFlag}
      />

      {pendingPromotion && (
        <StatusChangeConfirmDialog
          flag={pendingPromotion}
          onConfirm={handleConfirmPromotion}
          onCancel={() => setPendingPromotion(null)}
          isPending={isConfirmingPromotion}
        />
      )}
    </div>
  );
}
